import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { PersonFinal, PersonaActivity, AccountFinal, Note } from "@/api/entities";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User as UserIcon, ChevronLeft, GraduationCap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { createPageUrl } from "@/utils";
import { sanitizeId } from "@/components/utils/id";
import { applyTenantOverrides } from "@/components/utils/tenantOverrides";
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { useChatSession } from '@/contexts/ChatSessionContext';
import { resolveEffectiveTenant } from "@/lib/tenantUtils";
import { displayPersonaScore } from "@/lib/personaSignalScore";
import {
  personFieldToArray,
  formatPersonEducationEntry,
  sanitizePersonaRecord,
} from "@/lib/personFieldDisplay";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";

import ProfileHeader from "../components/person/ProfileHeader";
import ActivityTimeline from "../components/person/ActivityTimeline";
import InitiativesCard from "../components/person/InitiativesCard";
import PersonCompetitorsTabContent from "../components/competitors/PersonCompetitorsTabContent";
import Notes from "../components/accounts/Notes";
import PlaybooksTab from "../components/person/PlaybooksTab";
import OutreachComposer from "../components/OutreachComposer";
import OutboxDrawer from "../components/outbox/OutboxDrawer";
import { useTranslation } from '@/components/utils/translations';
import { collectPersonPublicationPathIdCandidates } from '@/lib/personPublicationPathIds';
import { personaSharesAlmaMater } from '@/lib/almaMaterMatch';
import { fetchPersonaCompetitiveProximity } from '@/lib/competitorsApi';
import { fetchUserUniversities } from '@/lib/userUniversities';
import { getBestPersonProfileLinkId } from '@/lib/personProfileLinkId';

// Accepts MongoDB ObjectId, dashed UUID, or 32-char hex UUID (no dashes — common in exports)
const isValidPersonId = (id) => {
  if (!id || typeof id !== 'string') return false;
  const trimmed = id.trim();
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return true;
  if (/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(trimmed)) return true;
  if (/^[a-f0-9]{32}$/i.test(trimmed)) return true;
  return false;
};

const PERSON_CACHE_KEY_PREFIX = 'person-profile-v5-';
const CACHE_DURATION = 5 * 60 * 1000;

function readPersonCache(personId, tenantId) {
  try {
    const raw = sessionStorage.getItem(PERSON_CACHE_KEY_PREFIX + (tenantId || 'default') + '-' + personId);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.cachedAt > CACHE_DURATION) {
      sessionStorage.removeItem(PERSON_CACHE_KEY_PREFIX + (tenantId || 'default') + '-' + personId);
      return null;
    }
    return data;
  } catch { return null; }
}

function writePersonCache(personId, tenantId, data) {
  try {
    sessionStorage.setItem(
      PERSON_CACHE_KEY_PREFIX + (tenantId || 'default') + '-' + personId,
      JSON.stringify({ ...data, cachedAt: Date.now() })
    );
  } catch {}
}

const toArray = personFieldToArray;

function connectionTypeLabel(type) {
  switch (type) {
    case 'university_cohort':
      return 'University';
    case 'former_colleague':
      return 'Former colleague';
    case 'professional_org':
      return 'Professional org';
    case 'co_author':
      return 'Co-author';
    case 'shared_membership':
      return 'Shared membership';
    case 'external_coauthor_at_client':
      return 'Co-author at client';
    default:
      return 'Connection';
  }
}

const AssignMarketTierModal = ({ persona, onClose, onSave, t }) => {
    const [selectedTier, setSelectedTier] = useState(persona?.market_tiers?.[0] || "");
    const marketTierOptions = ["Tier 1", "Tier 2", "Tier 3", "Tier 4"];

    const handleSubmit = () => {
        if (selectedTier) {
            onSave(selectedTier);
        }
        onClose();
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('assignMarketTier')}</DialogTitle>
                    <DialogDescription>
                        {t('selectTier')} {persona?.first_name} {persona?.last_name}.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="marketTier" className="text-right">
                            {t('tier')}
                        </Label>
                        <Select onValueChange={setSelectedTier} value={selectedTier}>
                            <SelectTrigger id="marketTier" className="col-span-3">
                                <SelectValue placeholder={t('selectTier')} />
                            </SelectTrigger>
                            <SelectContent>
                                {marketTierOptions.map(tier => (
                                    <SelectItem key={tier} value={tier}>
                                        {tier}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit}>{t('saveChanges')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function PersonProfile({ currentTenant: currentTenantProp, startChatSession }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const chatSession = useChatSession();
  const currentTenant = currentTenantProp || contextTenant;
  const registerPageAccount = chatSession?.registerPageAccount;
  const [persona, setPersona] = useState(null);
  const [account, setAccount] = useState(null);
  const [signals, setSignals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAssignMarketTierModal, setShowAssignMarketTierModal] = useState(false);
  // [2026-07-01] Init null (not 0) so the tab badge doesn't briefly show
  // "Interactions (0)" and then jump to the real number on first mount.
  // Render code below shows a tiny "…" while playbookCount === null.
  const [playbookCount, setPlaybookCount] = useState(null);
  const [notesCount, setNotesCount] = useState(0);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [connectionsData, setConnectionsData] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  /** `{ items, total }` when publications exist; `null` hides the section (incl. fetch failure). */
  const [publicationsBlock, setPublicationsBlock] = useState(null);
  const [publicationsExpanded, setPublicationsExpanded] = useState(false);
  const [outboxDraft, setOutboxDraft] = useState(null);
  const [userUniversities, setUserUniversities] = useState([]);
  const [competitorProximityRow, setCompetitorProximityRow] = useState(undefined);
  const { toast } = useToast();

  useEffect(() => {
    if (!registerPageAccount) return undefined;
    registerPageAccount(account);
    return () => registerPageAccount(null);
  }, [account, registerPageAccount]);

  const tabFromUrl = useMemo(
    () => new URLSearchParams(location.search).get('tab'),
    [location.search],
  );

  const almaMaterMatch = useMemo(
    () => (persona ? personaSharesAlmaMater(persona, userUniversities) : null),
    [persona, userUniversities]
  );

  const effectiveTenant = useMemo(
    () => resolveEffectiveTenant(
      contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
      currentTenant,
      contextUser,
      'instructure',
    ),
    [currentTenant, contextUser],
  );

  const showCompetitorsTab = Boolean(competitorProximityRow);
  const competitorTabCount = showCompetitorsTab ? (competitorProximityRow?.signal_count ?? 0) : 0;

  const defaultPersonTab = useMemo(() => {
    const allowed = ['overview', 'connections', 'signals', 'interactions', 'notes'];
    if (showCompetitorsTab) allowed.push('competitors');
    return allowed.includes(tabFromUrl) ? tabFromUrl : 'overview';
  }, [tabFromUrl, showCompetitorsTab]);

  const connectionsTabCount = connectionsCount + (almaMaterMatch ? 1 : 0);

  const publicationApiPersonLike = useMemo(
    () =>
      persona
        ? { id: persona.id, person_id: persona.person_id, _id: persona._id }
        : null,
    [persona?.id, persona?.person_id, persona?._id]
  );

  const composeFromChat = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const customContext = params.get('custom_context');
    return {
      customContext: customContext != null ? String(customContext) : '',
      autoGenerate: params.get('auto_generate') === '1',
    };
  }, [location.search]);

  const handleComposeAutoGenerateConsumed = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auto_generate') !== '1') return;
    params.delete('auto_generate');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!contextUser) return;
    const tenantId = resolveEffectiveTenant(
      contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
      currentTenant,
      contextUser,
      'instructure'
    );
    const userId = contextUser.id || contextUser.user_id;
    if (!userId) return;
    let cancelled = false;
    fetchUserUniversities(tenantId, userId).then((rows) => {
      if (!cancelled) setUserUniversities(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [contextUser, currentTenant]);

  useEffect(() => {
    const personId = persona?.person_id || persona?.id;
    if (!effectiveTenant || !personId) {
      setCompetitorProximityRow(null);
      return;
    }
    let cancelled = false;
    setCompetitorProximityRow(undefined);
    fetchPersonaCompetitiveProximity(effectiveTenant, personId, {})
      .then((data) => {
        if (!cancelled) setCompetitorProximityRow(data?.row ?? null);
      })
      .catch(() => {
        if (!cancelled) setCompetitorProximityRow(null);
      });
    return () => { cancelled = true; };
  }, [effectiveTenant, persona?.person_id, persona?.id]);

  // Fetch Interactions tab count when persona loads.
  // [2026-07-01] Radix Tabs only mounts inactive TabsContent on click, so the
  // <PlaybooksTab>'s onDataLoaded callback doesn't fire until the tab is
  // opened — the badge sat at 0 until then. We now pre-fetch here using the
  // same endpoints PlaybooksTab uses so the total matches what the user will
  // see when they open the tab: all-playbooks + all-outreach (ad-hoc).
  useEffect(() => {
    let mounted = true;
    const fetchInteractionsCount = async () => {
      if ((!persona?.person_id && !persona?.id) || !contextUser) return;

      try {
        const personId = persona.person_id || persona.id;
        const tenantId = resolveEffectiveTenant(
          contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
          currentTenant,
          contextUser,
          'instructure',
        );
        const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'https://api.neogtm.com'
          : 'https://api.neogtm.com';
        const userParam = contextUser?.id ? `&user_id=${encodeURIComponent(contextUser.id)}` : '';

        const [pbRes, ooRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/personas/${encodeURIComponent(personId)}/all-playbooks?tenant_id=${encodeURIComponent(tenantId)}${userParam}`,
            { credentials: 'include' },
          ),
          fetch(
            `${API_BASE_URL}/api/analytics-v2/personas/${encodeURIComponent(personId)}/all-outreach?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
            { credentials: 'include' },
          ).catch(() => null),
        ]);

        // [2026-07-01] Sum DELIVERED touches across playbooks — not the
        // number of playbooks. Zalizah is in 1 playbook but has 5 delivered
        // touches; the badge sat at 1 while the tab UI showed 5, which
        // looked like a bug. Now they agree.
        let deliveredTouches = 0;
        if (pbRes?.ok) {
          const pbData = await pbRes.json();
          const pbs = Array.isArray(pbData?.playbooks) ? pbData.playbooks : [];
          for (const pb of pbs) {
            deliveredTouches += Number(pb?.delivered_touches || 0);
          }
        }

        let oneOffCount = 0;
        if (ooRes?.ok) {
          const raw = await ooRes.json();
          const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw?.messages) ? raw.messages : Array.isArray(raw?.data) ? raw.data : [];
          oneOffCount = rows.length;
        }

        if (mounted) setPlaybookCount(deliveredTouches + oneOffCount);
      } catch (error) {
        if (mounted) {
          console.error('Error fetching interactions count:', error);
          setPlaybookCount(0);
        }
      }
    };

    if (persona) fetchInteractionsCount();
    return () => { mounted = false; };
    // [2026-07-01] Depend on STABLE primitives (ids/strings) — not the raw
    // `persona`, `currentTenant`, `contextUser` objects. Those objects get
    // new references on every parent re-render (e.g. from Context provider
    // updates, other state changes, or the parent's own re-fetch effects),
    // which made this effect re-run continually → each run briefly reset
    // the badge from the real count back to null → visible flicker in
    // the tab strip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona?.id, persona?.person_id, currentTenant, contextUser?.id]);

  // Notes tab count without forceMount on TabsContent (avoids mounting Notes while another tab is active).
  useEffect(() => {
    let cancelled = false;
    const syncNotesCount = async () => {
      if (!persona) return;
      const effectiveAccountId = String(account?.account_id || account?.id || '').trim() || null;
      const effectivePersonId = String(persona.id || '').trim() || null;
      const effectivePersonaId = String(persona.person_id || '').trim() || null;
      const personNoteMatchIds = [...new Set([effectivePersonId, effectivePersonaId].filter(Boolean))];
      const hasScope = Boolean(effectiveAccountId || personNoteMatchIds.length);

      if (!hasScope) {
        if (!cancelled) setNotesCount(0);
        return;
      }

      try {
        let list;
        if (effectiveAccountId && personNoteMatchIds.length > 0) {
          const raw = await Note.filter({ account_id: effectiveAccountId }, '-created_at');
          const rows = Array.isArray(raw) ? raw : [];
          const match = new Set(personNoteMatchIds.map(String));
          list = rows.filter((n) => {
            const pid = n.person_id != null ? String(n.person_id) : '';
            const paid = n.persona_id != null ? String(n.persona_id) : '';
            return (pid && match.has(pid)) || (paid && match.has(paid));
          });
        } else if (effectiveAccountId) {
          const raw = await Note.filter({ account_id: effectiveAccountId }, '-created_at');
          list = Array.isArray(raw) ? raw : [];
        } else {
          const scopeId = effectivePersonId || effectivePersonaId;
          const raw = await Note.filter({ person_id: scopeId }, '-created_at');
          list = Array.isArray(raw) ? raw : [];
        }
        if (!cancelled) setNotesCount(list.length);
      } catch (e) {
        console.error('Notes count sync failed:', e);
        if (!cancelled) setNotesCount(0);
      }
    };
    syncNotesCount();
    return () => {
      cancelled = true;
    };
  }, [persona, account]);

  useEffect(() => {
    let cancelled = false;
    const fetchConnections = async () => {
      if ((!persona?.person_id && !persona?.id) || !contextUser) return;
      const personId = persona.person_id || persona.id;
      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );
      const API_BASE_URL =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'https://api.neogtm.com'
          : 'https://api.neogtm.com';
      setConnectionsLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/personas/${encodeURIComponent(personId)}/connections?tenant_id=${encodeURIComponent(tenantId)}`,
          { credentials: 'include' }
        );
        if (response.ok && !cancelled) {
          const data = await response.json();
          const list = data.connections || [];
          setConnectionsData(list);
          setConnectionsCount(list.length);
        } else if (!cancelled) {
          setConnectionsData([]);
          setConnectionsCount(0);
        }
      } catch (e) {
        console.error('Error fetching connections:', e);
        if (!cancelled) {
          setConnectionsData([]);
          setConnectionsCount(0);
        }
      } finally {
        if (!cancelled) setConnectionsLoading(false);
      }
    };
    if (persona) fetchConnections();
    return () => {
      cancelled = true;
    };
  }, [persona, currentTenant, contextUser]);

  useEffect(() => {
    let cancelled = false;
    const loadPublications = async () => {
      if ((!persona?.person_id && !persona?.id) || !contextUser) {
        setPublicationsBlock(null);
        setPublicationsExpanded(false);
        return;
      }
      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );
      const API_BASE_URL =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'https://api.neogtm.com'
          : 'https://api.neogtm.com';
      const uid = contextUser?.user_id || contextUser?.id;
      const userQs = uid ? `&user_id=${encodeURIComponent(String(uid))}` : '';

      const urlQueryId = sanitizeId(new URLSearchParams(location.search).get('id'));
      const idCandidates = await collectPersonPublicationPathIdCandidates(
        { id: persona.id, person_id: persona.person_id, _id: persona._id },
        { urlQueryId }
      );

      const extractList = (data) => {
        if (!data || typeof data !== 'object') return [];
        if (Array.isArray(data.publications)) return data.publications;
        if (Array.isArray(data.data?.publications)) return data.data.publications;
        return [];
      };

      const extractHas = (data, list) => {
        if (list.length > 0) return true;
        const h = data?.has_publications ?? data?.data?.has_publications;
        if (h === true || h === 1) return true;
        if (typeof h === 'string' && h.toLowerCase() === 'true') return true;
        return false;
      };

      try {
        let chosen = null;
        for (const personId of idCandidates) {
          if (cancelled) return;
          const response = await fetch(
            `${API_BASE_URL}/api/personas/${encodeURIComponent(personId)}/publications?tenant_id=${encodeURIComponent(tenantId)}&limit=50${userQs}`,
            { credentials: 'include' }
          );
          if (!response.ok) continue;
          const data = await response.json();
          const list = extractList(data);
          const has = extractHas(data, list);
          if (has && list.length > 0) {
            chosen = {
              items: list,
              total: typeof data.count === 'number' ? data.count : typeof data.data?.count === 'number' ? data.data.count : list.length,
            };
            break;
          }
        }
        if (cancelled) return;
        if (chosen) {
          setPublicationsBlock(chosen);
          setPublicationsExpanded(false);
        } else {
          setPublicationsBlock(null);
          setPublicationsExpanded(false);
        }
      } catch {
        if (!cancelled) {
          setPublicationsBlock(null);
          setPublicationsExpanded(false);
        }
      }
    };
    loadPublications();
    return () => {
      cancelled = true;
    };
  }, [persona, currentTenant, contextUser, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const personId = sanitizeId(params.get('id'));
    
    console.log('🔍 PersonProfile loading with:', { personId });
    
    if (!personId) {
      console.log('⚠️ No person ID provided');
      setIsLoading(false);
      return;
    }

    if (!contextUser) {
      setIsLoading(false);
      return;
    }

    const loadPersonProfile = async () => {
      setIsLoading(true);

      try {
        const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');

        const cached = readPersonCache(personId, tenantId);
        if (cached && cached.persona && cached.account) {
          console.log('📦 PersonProfile: Using cache');
          setPersona(sanitizePersonaRecord(cached.persona));
          setAccount(cached.account);
          setSignals(cached.signals || []);
          setIsLoading(false);
          return;
        }
        
        console.log('🏢 PersonProfile tenant:', { tenantId, currentTenant, userTenant: contextUser?.tenant_id });
      
        // STEP 1: Resolve row — try entity id first, then person_id (some tenants/links use business keys)
        let foundPerson = null;

        if (isValidPersonId(personId)) {
          try {
            const byId = await PersonFinal.filter({ id: personId });
            if (byId && byId.length > 0) {
              foundPerson = byId[0];
              console.log('✅ Found persona by id:', foundPerson.first_name, foundPerson.last_name);
            }
          } catch (e) {
            console.error('❌ Error fetching persona by id:', e);
          }
        } else {
          // Business keys like "psilvafilho" should go straight to person_id lookup.
          console.log('ℹ️ Skipping PersonFinal id lookup for non-DB identifier:', personId);
        }

        if (!foundPerson) {
          try {
            const byPersonKey = await PersonFinal.filter({ person_id: personId });
            if (byPersonKey && byPersonKey.length > 0) {
              foundPerson = byPersonKey[0];
              console.log('✅ Found persona by person_id:', foundPerson.first_name, foundPerson.last_name);
            }
          } catch (e) {
            console.error('❌ Error fetching persona by person_id:', e);
          }
        }

        // Fallback for tenant-scoped/override records where direct filter misses.
        if (!foundPerson) {
          try {
            const allPeople = await PersonFinal.list();
            const tenantMatches = (record) => {
              if (!record) return false;
              if (tenantId === 'smowl') return record.tenant_id === 'smowl';
              if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
              if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
              if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
              if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
              if (tenantId === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
              if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
              if (tenantId === 'faria') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
              if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
              if (tenantId === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
              if (tenantId === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
              if (tenantId === 'skillwell') return record.tenant_id === 'skillwell' || record.tenant_id === 'skillwise' || record.tenant_id === 'global';
              if (tenantId === 'kairadigital') return record.tenant_id === 'kairadigital';
              if (tenantId === 'plataformaa') return record.tenant_id === 'plataformaa';
              if (tenantId === 'learnwise') return record.tenant_id === 'learnwise' || record.tenant_id === 'global';
              if (tenantId === 'funidata') return record.tenant_id === 'funidata' || record.tenant_id === 'global';
              if (tenantId === 'akari') return record.tenant_id === 'akari' || record.tenant_id === 'global';
              if (tenantId === 'almentor') return record.tenant_id === 'almentor' || record.tenant_id === 'global';
              if (tenantId === 'gravyty') return record.tenant_id === 'gravyty' || record.tenant_id === 'global';
              if (tenantId === 'infonetica') return record.tenant_id === 'infonetica' || record.tenant_id === 'global';
              if (tenantId === 'cayuse') return record.tenant_id === 'cayuse' || record.tenant_id === 'global';
              if (tenantId === 'uplanner') return record.tenant_id === 'uplanner' || record.tenant_id === 'global';
              if (tenantId === 'edusign') return record.tenant_id === 'edusign' || record.tenant_id === 'global';
              if (tenantId === 'liaison') return record.tenant_id === 'liaison' || record.tenant_id === 'global';
              return false;
            };

            const fallbackMatch = (allPeople || []).find((p) => {
              if (!tenantMatches(p)) return false;
              return String(p.id || '') === String(personId) || String(p.person_id || '') === String(personId);
            });

            if (fallbackMatch) {
              foundPerson = fallbackMatch;
              console.log('✅ Found persona via tenant fallback scan:', foundPerson.first_name, foundPerson.last_name);
            }
          } catch (e) {
            console.error('❌ Error in persona fallback scan:', e);
          }
        }

        if (!foundPerson && !/^\d{1,4}$/.test(String(personId))) {
          const API_BASE_URL_LOCAL =
            window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
              ? 'https://api.neogtm.com'
              : 'https://api.neogtm.com';
          const userParam = contextUser?.id ? `&user_id=${encodeURIComponent(contextUser.id)}` : '';
          try {
            const res = await fetch(
              `${API_BASE_URL_LOCAL}/api/analytics-v2/persona/${encodeURIComponent(personId)}?tenant_id=${encodeURIComponent(tenantId)}${userParam}`,
              { credentials: 'include' },
            );
            if (res.ok) {
              const dbPerson = await res.json();
              const stablePersonId = dbPerson.person_id || dbPerson.persona_id || dbPerson.persona_key || personId;
              if (stablePersonId && stablePersonId !== personId) {
                try {
                  const bySlug = await PersonFinal.filter({ person_id: stablePersonId });
                  if (bySlug?.length > 0) foundPerson = bySlug[0];
                } catch { /* fall through to analytics row */ }
              }
              if (!foundPerson) {
                foundPerson = {
                  ...dbPerson,
                  id: dbPerson.id || personId,
                  person_id: stablePersonId,
                  persona_id: dbPerson.persona_id || stablePersonId,
                  first_name: dbPerson.first_name || dbPerson.full_name?.split?.(' ')?.[0] || '',
                  last_name: dbPerson.last_name || dbPerson.full_name?.split?.(' ')?.slice(1).join(' ') || '',
                };
              }
            }
          } catch (e) {
            console.error('❌ analytics-v2/persona fallback failed:', e);
          }
        }

        if (!foundPerson && !isValidPersonId(personId)) {
          toast({
            title: "Invalid Profile Link",
            description: "The profile link is not a valid person identifier.",
            variant: "destructive",
          });
          setPersona(null);
          setAccount(null);
          setSignals([]);
          setIsLoading(false);
          return;
        }

        if (!foundPerson) {
          console.log('❌ Persona not found for id/person_id:', personId);
          toast({
            title: "Persona Not Found",
            description: "The requested persona profile could not be found.",
            variant: "destructive",
          });
          setPersona(null);
          setAccount(null);
          setSignals([]);
          setIsLoading(false);
          return;
        }
      
        // Apply tenant overrides and check visibility
        foundPerson = applyTenantOverrides(foundPerson, tenantId);
        if (foundPerson.is_visible === false) {
          console.log('❌ Person is not visible:', personId);
          setPersona(null);
          setAccount(null);
          setSignals([]);
          setIsLoading(false);
          return;
        }
      
        // STEP 2: Find the linked account from persona record
        // We no longer use accountId from URL params - resolve from persona.account_id
        let linkedAccount = null;
        
        if (foundPerson.account_id) {
          try {
            // First try account_id field (e.g., 'australia_001_006')
            const accByAccountId = await AccountFinal.filter({ account_id: foundPerson.account_id });
            if (accByAccountId && accByAccountId.length > 0) {
              linkedAccount = applyTenantOverrides(accByAccountId[0], tenantId);
              console.log('✅ Found account by account_id field:', linkedAccount.account_name);
            } else {
              // Only try as UUID if it looks like one
              const looksLikeUuid = /^[a-f0-9-]{36}$/i.test(foundPerson.account_id);
              if (looksLikeUuid) {
                const accById = await AccountFinal.filter({ id: foundPerson.account_id });
                if (accById && accById.length > 0) {
                  linkedAccount = applyTenantOverrides(accById[0], tenantId);
                  console.log('✅ Found account by id:', linkedAccount.account_name);
                }
              }
            }
          } catch (e) {
            console.log('🔍 Account lookup by person.account_id failed');
          }
        }
        
        // Final fallback: try by account_name
        if (!linkedAccount && foundPerson.account_name) {
          try {
            const accByName = await AccountFinal.filter({ account_name: foundPerson.account_name });
            if (accByName && accByName.length > 0) {
              linkedAccount = applyTenantOverrides(accByName[0], tenantId);
              console.log('✅ Found account by name:', linkedAccount.account_name);
            }
          } catch (e) {
            console.log('🔍 Account lookup by name failed');
          }
        }
      
        // Check account visibility
        if (linkedAccount && linkedAccount.is_visible === false) {
          linkedAccount = null;
        }
      
        // STEP 3: Load activities for this person
        let personActivities = [];
        try {
          const activitiesRaw = await PersonaActivity.filter({ 
            person_id: foundPerson.person_id || foundPerson.id 
          }, '-date_published');
          
          personActivities = (activitiesRaw || [])
            .slice(0, 20)
            .map(activity => ({
              id: activity.id,
              title: activity.summary || "Activity Update",
              description: activity.news_source || "",
              source_url: activity.news_source_url || activity.full_url,
              signal_type: activity.signal_type || 'profile_update',
              signal_date: activity.date_published,
              impact_score: displayPersonaScore(activity),
              persona_signal_score: activity.persona_signal_score,
              tags: []
            }));
        } catch (e) {
          console.log('🔍 Activities lookup failed:', e);
        }
      
        // STEP 4: Prepare final persona object
        let finalPersona = { ...foundPerson };
        if (!finalPersona.country && linkedAccount?.country) {
          finalPersona.country = linkedAccount.country;
        }
        finalPersona.account_id = linkedAccount?.id || foundPerson.account_id || null;
        finalPersona = sanitizePersonaRecord(finalPersona);
      
        console.log('✅ PersonProfile data prepared:', {
          person: `${finalPersona.first_name} ${finalPersona.last_name}`,
          personDatabaseId: finalPersona.id,
          personIdField: finalPersona.person_id,
          account: linkedAccount?.account_name,
          accountDbId: linkedAccount?.id,
          accountFieldId: linkedAccount?.account_id,
          country: finalPersona.country,
          signals: personActivities.length,
          tenant: finalPersona.tenant_id
        });
      
        setPersona(finalPersona);
        setAccount(linkedAccount);
        setSignals(personActivities);
      
        writePersonCache(personId, tenantId, {
          persona: finalPersona,
          account: linkedAccount,
          signals: personActivities
        });

      } catch (error) {
        console.error('❌ Error loading person profile:', error);
        setPersona(null);
        setAccount(null);
        setSignals([]);
        toast({
          title: "Error loading profile",
          description: error.message || "An unexpected error occurred while loading the profile.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadPersonProfile();
  }, [location.search, currentTenant, contextUser, toast]);

  const handleAddToPursuit = async () => {
    if (!persona) return;

    const personId = persona.person_id || persona.id;
    
    console.log('🎯 Attempting pursuit toggle for:', {
      personId,
      name: `${persona.first_name} ${persona.last_name}`,
      currentStatus: persona.add_to_pursuit
    });

    try {
      const newStatus = !persona.add_to_pursuit;
      const tenantId = resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure');
      
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com' 
        : 'https://api.neogtm.com';

      const url = `${API_BASE_URL}/api/pursuit/${encodeURIComponent(personId)}`;
      
      let response;
      if (newStatus) {
        // Add to pursuit
        response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            user_id: contextUser?.id, 
            tenant_id: tenantId 
          })
        });
      } else {
        // Remove from pursuit
        response = await fetch(`${url}?user_id=${contextUser?.id}&tenant_id=${tenantId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update pursuit status');
      }

      console.log('✅ Pursuit update successful');
      
      // Update local state
      const updatedPersona = { ...persona, add_to_pursuit: newStatus };
      setPersona(sanitizePersonaRecord(updatedPersona));
      
      // Update cache
      const params = new URLSearchParams(window.location.search);
      const cachePersonId = sanitizeId(params.get('id'));
      if (cachePersonId) {
        const cached = readPersonCache(cachePersonId, tenantId);
        if (cached) {
          writePersonCache(cachePersonId, tenantId, { ...cached, persona: updatedPersona });
        }
      }
      
      // Clear home page cache so Watchlist refreshes
      try {
        sessionStorage.removeItem('home-cache-v2');
      } catch {}
      
      toast({
        title: newStatus ? "Added to Pursuit" : "Removed from Pursuit",
        description: `${persona.first_name} ${persona.last_name} ${newStatus ? 'is now being tracked' : 'has been removed from tracking'}.`,
      });
    } catch (error) {
      console.error("Pursuit toggle error:", error);
      toast({
        title: "Error",
        description: `Could not update pursuit status: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleAssignMarketTier = async (newTier) => {
    if (!persona) return;

    try {
        const updatedPersona = await PersonFinal.update(persona.id, { market_tiers: [newTier] });
        setPersona((prev) =>
          sanitizePersonaRecord({ ...prev, market_tiers: updatedPersona.market_tiers })
        );
        toast({
            title: "Market Tier Updated",
            description: `${persona.first_name}'s market tier has been set to ${newTier}.`,
        });
    } catch (error) {
        console.error("Failed to update market tier:", error);
        toast({
            title: "Error",
            description: "Could not update the market tier.",
            variant: "destructive",
        });
    }
    setShowAssignMarketTierModal(false);
  };

  const handleDraftOutreach = () => {
    if (!persona || !account || !startChatSession) return;
    
    startChatSession({
      directToOutreach: true,
      preselectedAccount: account,
      preselectedPersona: persona
    });
  };

  // CRITICAL: Validate account.id is a valid MongoDB ObjectId format (24 hex chars) or proper account_id
  // This prevents using person IDs as account IDs
  const isValidAccountId = (id) => {
    if (!id) return false;
    if (/^[a-f0-9]{24}$/i.test(id)) return true;
    if (typeof id === 'string' && id.includes('_')) return true;
    if (/^\d+$/.test(String(id))) return true;
    return false;
  };

  // Try to create account URL using account.id (MongoDB ObjectId) or account.account_id
  // resolveAccount in AccountFinal can handle both UUIDs and account_name, but for account_id strings
  // we need to use the account_id directly. However, AccountFinal expects 'id' parameter which
  // resolveAccount will handle. For MongoDB ObjectIds (24 hex chars), use id. For account_id strings,
  // we can also pass them and resolveAccount will try account_name as fallback.
  const accountUrl = account && (
    (account.id && isValidAccountId(account.id))
      ? createPageUrl(`AccountFinal?id=${account.id}`)
      : (account.account_id)
        ? createPageUrl(`AccountFinal?id=${encodeURIComponent(account.account_id)}`)
        : null
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  /* ── Back goes where they CAME FROM, not always to People ──────────────────────────────────
   *
   * This was a hard link to the People list, so a rep who opened a person from an account, from
   * today's plan, from the Market competitor table or from a playbook was thrown to a list they
   * had never been on — and the only way back to their actual place was the browser chrome.
   *
   * 🔴 FIXED HERE RATHER THAN AT THE CALLERS. There are 14+ places that link to this page across
   * Home, Accounts, Market and Playbooks, none of which passes any origin, and more get added.
   * Threading a `from` through every one of them is a change that is wrong the first time someone
   * forgets it, and the forgetting is silent. The router already knows where the user came from.
   *
   * `window.history.state.idx` is react-router's own position in the session history: > 0 means
   * there IS an in-app entry behind this one, so `navigate(-1)` lands exactly where they were,
   * with that page's own scroll and state restored. idx 0 means this page IS the first entry —
   * a deep link from the MCP connector, an emailed link, a refresh, a new tab — where there is
   * nothing to go back to and People is the right destination.
   *
   * Read at render rather than cached: a person-to-person click pushes a new entry, and the
   * control has to describe the history as it is now. */
  const hasHistoryBehind = typeof window !== 'undefined'
    && typeof window.history?.state?.idx === 'number'
    && window.history.state.idx > 0;

  /* An element, not a component: a component declared in a render body is a new type on every
   * pass, which remounts it and loses focus. */
  const backLinkClass = 'inline-flex items-center gap-2 text-sm font-medium text-[#5C6B7A] hover:text-[#0A1A2F]';
  const backControl = hasHistoryBehind ? (
    <button type="button" onClick={() => navigate(-1)} className={backLinkClass}>
      <ChevronLeft className="w-4 h-4" />
      {t('back')}
    </button>
  ) : (
    /* Only label it "Back to People" when that is genuinely where it goes. */
    <Link to={createPageUrl('People')} className={backLinkClass}>
      <ChevronLeft className="w-4 h-4" />
      {t('backToPeople')}
    </Link>
  );

  if (!persona) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="mb-4">
          {backControl}
        </div>
        <div className="text-center py-20">
          <UserIcon className="w-12 h-12 mx-auto text-gray-300" />
          <h2 className="mt-4 text-xl font-semibold text-[#0A1A2F]">{t('personNotFound')}</h2>
          <p className="text-[#5C6B7A]">{t('personNotFoundDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="mb-4">
          {backControl}
        </div>

        <div className="space-y-4 md:space-y-6">
          <ProfileHeader person={persona} account={account} accountUrl={accountUrl} onPersonUpdate={(updated) => setPersona((prev) => sanitizePersonaRecord({ ...prev, ...updated }))} tenantId={resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure')} almaMaterMatch={almaMaterMatch} />

          <Tabs defaultValue={defaultPersonTab} key={defaultPersonTab}>
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <TabsList className="inline-flex w-auto min-w-full md:min-w-0" style={{ backgroundColor: '#ECFFFE' }}>
                <TabsTrigger value="overview" className="flex-1 md:flex-initial">{t('overview')}</TabsTrigger>
                <TabsTrigger value="connections" className="flex-1 md:flex-initial">
                  {t('connections')} ({connectionsTabCount})
                </TabsTrigger>
                <TabsTrigger value="signals" className="flex-1 md:flex-initial">{t('signalsCount')} ({signals.length})</TabsTrigger>
                {/* [2026-07-01] Hide the (N) parens entirely while the
                    pre-fetch is in flight. Showing "(…)" or "(0)" first
                    both read as a flicker; showing just the label until
                    the real number is known reads as calm. */}
                <TabsTrigger value="interactions" className="flex-1 md:flex-initial">
                  {t('interactions')}{playbookCount != null ? ` (${playbookCount})` : ''}
                </TabsTrigger>
                {showCompetitorsTab ? (
                  <TabsTrigger value="competitors" className="flex-1 md:flex-initial">
                    Competitors ({competitorTabCount})
                  </TabsTrigger>
                ) : null}
                <TabsTrigger value="notes" className="flex-1 md:flex-initial">{t('notes')} ({notesCount})</TabsTrigger>
              </TabsList>
            </div>
            
            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {toArray(persona.initiatives, { splitEntries: false }).length > 0 && (
                    <InitiativesCard initiatives={toArray(persona.initiatives, { splitEntries: false })} />
                  )}

                  {toArray(persona.primary_interests).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">{t('primaryInterests')}</h3>
                        <div className="flex flex-wrap gap-2">
                          {toArray(persona.primary_interests).map((interest, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.former_employers).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">{t('formerEmployers')}</h3>
                        <ul className="space-y-2">
                          {toArray(persona.former_employers).map((employer, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {employer}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.education).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">{t('education')}</h3>
                        <ul className="space-y-2">
                          {toArray(persona.education)
                            .map((entry, idx) => ({
                              idx,
                              label: formatPersonEducationEntry(entry),
                            }))
                            .filter(({ label }) => label)
                            .map(({ idx, label }) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {label}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.memberships).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">
                          {t('professionalMemberships')}
                        </h3>
                        <ul className="space-y-2">
                          {toArray(persona.memberships).map((membership, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {membership}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.public_speaking).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">
                          {t('publicSpeakingEvents')}
                        </h3>
                        <ul className="space-y-2">
                          {toArray(persona.public_speaking).map((event, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {event}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.events_attended).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">
                          {t('recentEventsAttended')}
                        </h3>
                        <ul className="space-y-2">
                          {toArray(persona.events_attended).map((event, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {event}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {toArray(persona.awards).length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">
                          {t('awardsRecognition')}
                        </h3>
                        <ul className="space-y-2">
                          {toArray(persona.awards).map((award, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-[#5C6B7A] flex items-center gap-2"
                            >
                              <span className="w-1.5 h-1.5 bg-[#00C2C2] rounded-full"></span>
                              {award}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {publicationsBlock && publicationsBlock.items.length > 0 && (
                    <Card className="border-0 shadow-md">
                      <CardContent className="p-4">
                        <h3 className="font-semibold mb-3">{t('publications')}</h3>
                        <ul className="space-y-4">
                          {(publicationsExpanded
                            ? publicationsBlock.items
                            : publicationsBlock.items.slice(0, 10)
                          ).map((pub) => {
                            const y = pub.year != null && pub.year !== '' ? String(pub.year) : null;
                            const pubName = pub.publisher != null && String(pub.publisher).trim() !== ''
                              ? String(pub.publisher).trim()
                              : null;
                            const meta = [y, pubName].filter(Boolean).join(' · ');
                            const tier = pub.relevance_tier;
                            const showTier = tier === 'very_strong' || tier === 'strong';
                            return (
                              <li key={pub.id || `${pub.title}-${y}`} className="text-sm text-[#0A1A2F]">
                                <div className="font-medium text-[#0A1A2F] leading-snug">{pub.title}</div>
                                {meta && (
                                  <div className="text-xs text-[#5C6B7A] mt-1">{meta}</div>
                                )}
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                  {showTier && (
                                    <Badge variant="outline" className="text-[10px] font-normal">
                                      {tier === 'very_strong'
                                        ? t('relevanceTierVeryStrong')
                                        : t('relevanceTierStrong')}
                                    </Badge>
                                  )}
                                  {pub.is_first_author === true && (
                                    <Badge variant="secondary" className="text-[10px] font-normal">
                                      {t('firstAuthorBadge')}
                                    </Badge>
                                  )}
                                  {pub.is_last_author === true && (
                                    <Badge variant="secondary" className="text-[10px] font-normal">
                                      {t('seniorAuthorBadge')}
                                    </Badge>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                        {publicationsBlock.total > 10 && (
                          <div className="mt-4">
                            {!publicationsExpanded ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={() => setPublicationsExpanded(true)}
                              >
                                {t('showAllPublications', { count: publicationsBlock.total })}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-xs text-[#5C6B7A]"
                                onClick={() => setPublicationsExpanded(false)}
                              >
                                {t('showLess')}
                              </Button>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>

                
                <div className="space-y-6">
                  {/* AI Outreach Composer */}
                  <OutreachComposer
                    tenantId={resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure')}
                    userId={contextUser?.user_id || contextUser?.id}
                    personId={persona.id || persona.person_id}
                    publicationPersonIdentity={publicationApiPersonLike}
                    accountForProductContext={account}
                    personaForProductContext={persona}
                    accountId={account?.id}
                    personName={`${persona.first_name} ${persona.last_name}`}
                    personFirstName={persona.first_name}
                    personEmail={persona.email}
                    personLinkedin={persona.linkedin_url}
                    personPhone={persona.phone}
                    accountName={account?.account_name || persona.account_name}
                    archetype={persona.archetype_name}
                    initialCustomContext={composeFromChat.customContext}
                    autoGenerateOnMount={composeFromChat.autoGenerate}
                    onAutoGenerateConsumed={handleComposeAutoGenerateConsumed}
                    onDraftCreated={(draft) => {
                      console.log('📬 Opening outbox with draft:', JSON.stringify(draft, null, 2));
                      setOutboxDraft(draft);
                    }}
                  />

                  <Card className="border-0 shadow-md">
                    <CardContent className="p-4 space-y-2">
                      <Button 
                        onClick={handleAddToPursuit}
                        size="sm" 
                        className={persona.add_to_pursuit ? "w-full bg-purple-600 hover:bg-purple-700 text-white" : "w-full bg-blue-500 hover:bg-blue-600 text-white"}
                      >
                        {persona.add_to_pursuit ? t('removeFromPursuit') : t('addToPursuit')}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="connections" className="mt-6">
              {almaMaterMatch && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-teal-50 border border-teal-200 mb-4">
                  <GraduationCap className="w-4 h-4 text-teal-700 shrink-0" />
                  <span className="text-sm text-teal-900">
                    You both{' '}
                    {almaMaterMatch.matched_via === 'employer' ? 'have a tie to' : 'attended'}{' '}
                    <strong>{almaMaterMatch.matched_university}</strong>.
                  </span>
                </div>
              )}
              {connectionsLoading && <Skeleton className="h-32 w-full" />}
              {!connectionsLoading && connectionsData.length === 0 && !almaMaterMatch && (
                <div className="text-center py-12 text-[#5C6B7A]">
                  <UserIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">{t('connectionsEmpty')}</p>
                </div>
              )}
              {!connectionsLoading && connectionsData.length > 0 && (
                <div className="space-y-3">
                  {connectionsData.map((c) => {
                    const isExternal = c.connection_type === 'external_coauthor_at_client';
                    const isUnopenable = isExternal || c.outside_selection === true;
                    const profileLinkId = !isUnopenable ? getBestPersonProfileLinkId(c) : null;
                    const cardKey = c.external_id || c.person_id || `${c.connection_type}-${c.name}`;

                    return (
                      <Card
                        key={cardKey}
                        className={`border-0 shadow-md ${isExternal ? 'border-l-4 border-l-[#00C2C2]' : ''}`}
                      >
                        <CardContent className="p-4 flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {isUnopenable || !profileLinkId ? (
                              <div className="font-semibold text-[#0A1A2F] truncate">
                                {c.name}
                              </div>
                            ) : (
                              <Link
                                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(profileLinkId)}`)}
                                className="font-semibold text-[#0A1A2F] hover:text-[#00C2C2] truncate block"
                              >
                                {c.name}
                              </Link>
                            )}

                            {c.role_title && (
                              <div className="text-sm text-[#5C6B7A] truncate">{c.role_title}</div>
                            )}
                            {c.account_name && (
                              <div className="text-xs text-gray-500 truncate">{c.account_name}</div>
                            )}

                            {isExternal && c.publication_title && (
                              <div className="text-xs text-gray-500 mt-1 truncate">
                                <span className="italic">{c.publication_title}</span>
                                {c.publication_year ? ` · ${c.publication_year}` : ''}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant="outline" className="text-xs">
                              {connectionTypeLabel(c.connection_type)}
                            </Badge>

                            {isExternal && c.client_name && (
                              <Badge
                                className="text-xs bg-[#00C2C2]/10 text-[#00C2C2] border border-[#00C2C2]/30 hover:bg-[#00C2C2]/15"
                                variant="outline"
                              >
                                Client · {c.client_name}
                              </Badge>
                            )}

                            {!isExternal && c.institution && (
                              <span className="text-xs text-gray-700 text-right">
                                {c.institution}
                                {c.cohort_label && (
                                  <span className="text-gray-500"> · {c.cohort_label}</span>
                                )}
                              </span>
                            )}

                            {!isExternal && !c.institution && c.notes && (
                              <span className="text-xs text-gray-700 text-right max-w-[320px] line-clamp-3">
                                {c.notes}
                              </span>
                            )}

                            {c.is_inferred && (
                              <span className="text-[10px] text-gray-400 italic">{t('connectionInferred')}</span>
                            )}

                            {isExternal && c.match_method === 'fuzzy' && (
                              <span className="text-[10px] text-amber-600 italic">
                                fuzzy match ({Math.round((c.match_score || 0) * 100)}%)
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="signals" className="mt-6">
              <ActivityTimeline signals={signals} />
            </TabsContent>

            {showCompetitorsTab ? (
              <TabsContent value="competitors" className="mt-6">
                <PersonCompetitorsTabContent
                  tenantId={effectiveTenant}
                  personId={persona.person_id || persona.id}
                />
              </TabsContent>
            ) : null}

            <TabsContent value="interactions" className="mt-6">
              <PlaybooksTab personId={persona.person_id || persona.id} currentTenant={currentTenant} onDataLoaded={(count) => setPlaybookCount(count)} />
            </TabsContent>
            
            <TabsContent value="notes" className="mt-6">
              <Notes
                accountId={account?.account_id || account?.id}
                personId={persona.id}
                personaId={persona.person_id}
                onNotesCountChange={setNotesCount}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {showAssignMarketTierModal && (
        <AssignMarketTierModal
          persona={persona}
          onClose={() => setShowAssignMarketTierModal(false)}
          onSave={handleAssignMarketTier}
          t={t}
        />
      )}

      {/* Outbox Drawer Modal */}
      {outboxDraft && (
        <OutboxDrawer
          draft={{
            ...outboxDraft,
            // Ensure persona_id is set (primary reference to personas table - source of truth)
            persona_id: outboxDraft.persona_id || outboxDraft.person_id || persona?.person_id || persona?.id,
            // Ensure person object has required fields
            person: {
              ...outboxDraft.person,
              name: outboxDraft.person?.name || outboxDraft.person?.full_name || outboxDraft.person_name || `${persona?.first_name || ''} ${persona?.last_name || ''}`.trim(),
              full_name: outboxDraft.person?.full_name || outboxDraft.person_name || `${persona?.first_name || ''} ${persona?.last_name || ''}`.trim(),
              first_name: outboxDraft.person?.first_name || persona?.first_name,
              last_name: outboxDraft.person?.last_name || persona?.last_name,
              // Contact data from personas table (source of truth) - OutboxDrawer will fetch fresh
              email: outboxDraft.person?.email || outboxDraft.person_email || persona?.email,
              linkedin_url: outboxDraft.person?.linkedin_url || outboxDraft.linkedin_url || persona?.linkedin_url,
              phone: outboxDraft.person?.phone || outboxDraft.phone || persona?.phone,
              persona_id: outboxDraft.person?.persona_id || outboxDraft.persona_id || outboxDraft.person?.person_id || outboxDraft.person_id || persona?.person_id || persona?.id,
              person_id: outboxDraft.person?.person_id || outboxDraft.person_id || persona?.person_id || persona?.id, // Keep for backward compatibility
              id: outboxDraft.person?.id || outboxDraft.person_id || persona?.person_id || persona?.id
            },
            // Ensure account object has required fields
            account: {
              ...outboxDraft.account,
              name: outboxDraft.account?.name || outboxDraft.account?.account_name || outboxDraft.account_name || account?.account_name,
              account_name: outboxDraft.account?.account_name || outboxDraft.account_name || account?.account_name,
              id: outboxDraft.account?.id || outboxDraft.account_id || account?.id
            },
            // Ensure other required fields
            person_name: outboxDraft.person_name || `${persona?.first_name || ''} ${persona?.last_name || ''}`.trim(),
            person_email: outboxDraft.person_email || persona?.email,
            linkedin_url: outboxDraft.linkedin_url || persona?.linkedin_url,
            phone: outboxDraft.phone || persona?.phone,
            account_name: outboxDraft.account_name || account?.account_name,
            person_id: outboxDraft.person_id || persona?.person_id || persona?.id, // Keep for backward compatibility
            account_id: outboxDraft.account_id || account?.id
          }}
          tenantId={resolveEffectiveTenant(contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null, currentTenant, contextUser, 'instructure')}
          userId={contextUser?.id}
          onClose={() => setOutboxDraft(null)}
          onSent={() => {
            setOutboxDraft(null);
            toast({
              title: 'Message sent',
              description: 'Your message has been sent successfully'
            });
          }}
        />
      )}
    </div>
  );
}